/**
 * Relative time formatting utilities.
 *
 * Uses `Intl.RelativeTimeFormat` for locale-aware output.
 * Falls back to simple English strings for very old dates.
 */

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 7],
  ['week', 4.345],
  ['month', 12],
  ['year', Infinity],
];

let rtf: Intl.RelativeTimeFormat | null = null;

function getFormatter() {
  if (!rtf) {
    rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto', style: 'narrow' });
  }
  return rtf;
}

/**
 * Format a date as a relative time string ("2 min. ago", "yesterday", "3 mo. ago").
 * Returns `null` if the input is falsy.
 */
export function formatRelativeTime(date: string | Date | null | undefined): string | null {
  if (!date) return null;

  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return null;

  let seconds = Math.round((d.getTime() - Date.now()) / 1000);

  // "Just now" for very recent
  if (Math.abs(seconds) < 10) return 'just now';

  const formatter = getFormatter();

  for (const [unit, max] of UNITS) {
    if (Math.abs(seconds) < max) {
      return formatter.format(Math.round(seconds), unit);
    }
    seconds /= max;
  }

  return formatter.format(Math.round(seconds), 'year');
}

/**
 * Format a date as a full readable string for tooltips.
 */
export function formatFullDate(date: string | Date | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  return d.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
